/**
 * Breadcrumbs — route-driven back-navigation bar shown above every
 * authenticated page.
 *
 * Iter72.4 — user feedback: "no obvious way to navigate back from many
 * screens." Rather than retrofit a <Back> button into every page,
 * render a single breadcrumb strip inside Layout. Driven by
 * `useLocation()` so new routes get breadcrumbs for free.
 *
 * Mobile: a compact "← Dashboard" pill (last crumb only) because the
 * full trail doesn't fit. Desktop: full "Dashboard › Shifts › Edit".
 */
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ArrowLeft, Home } from 'lucide-react';

// Human labels for route segments. New segments get a Title-Cased
// fallback automatically, so we only list the ones that need a custom
// label or a non-obvious capitalisation.
const SEGMENT_LABELS = {
  '': 'Dashboard',
  dashboard: 'Dashboard',
  schedule: 'Schedule',
  shifts: 'Shifts',
  medics: 'Users',
  contracts: 'Contracts',
  'job-types': 'Rates',
  leave: 'Leave',
  expenses: 'Expenses',
  reports: 'Reports',
  'audit-logs': 'Audit Logs',
  settings: 'Settings',
  'shift-requests': 'Shift Requests',
  compliance: 'Compliance',
  'live-map': 'Live Map',
  help: 'Help',
  portal: 'My Portal',
  availability: 'Availability',
  documents: 'Documents',
  profile: 'Profile',
  'open-shifts': 'Open Shifts',
  admin: 'Admin',
  platform: 'Platform',
};

// Routes where a breadcrumb trail doesn't help (first-time flows,
// one-screen pages, or where the sidebar is already the only nav).
const HIDDEN_ROUTES = new Set([
  '/login', '/register', '/forgot-password', '/select-role',
  '/trial-pending', '/invitations/accept',
  '/payment/success', '/payment/cancel',
]);

function labelFor(segment) {
  if (SEGMENT_LABELS[segment] != null) return SEGMENT_LABELS[segment];
  // uuid-ish detail segment → just "Details"
  if (/^[0-9a-f-]{8,}$/i.test(segment)) return 'Details';
  return segment
    .split('-')
    .map(w => w ? w[0].toUpperCase() + w.slice(1) : '')
    .join(' ');
}

export function Breadcrumbs() {
  const { pathname } = useLocation();

  const crumbs = useMemo(() => {
    if (HIDDEN_ROUTES.has(pathname)) return null;
    const segs = pathname.split('/').filter(Boolean);
    if (segs.length === 0) return null; // nothing to show at "/"
    // Dashboard is the implicit root for every authed section —
    // except the medic portal, which has its own home.
    const rootHref = segs[0] === 'portal' ? '/portal' : '/dashboard';
    const rootLabel = segs[0] === 'portal' ? 'My Portal' : 'Dashboard';
    const trail = [{ href: rootHref, label: rootLabel }];
    if (segs[0] === 'dashboard' || (segs[0] === 'portal' && segs.length === 1)) {
      return trail; // on the root itself — just show "Dashboard"
    }
    // Build incremental paths for every segment after the root.
    let href = segs[0] === 'portal' ? '/portal' : '';
    const start = segs[0] === 'portal' ? 1 : 0;
    for (let i = start; i < segs.length; i++) {
      href += `/${segs[i]}`;
      trail.push({ href, label: labelFor(segs[i]) });
    }
    return trail;
  }, [pathname]);

  if (!crumbs || crumbs.length === 0) return null;
  const last = crumbs[crumbs.length - 1];
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;

  return (
    <nav
      aria-label="breadcrumb"
      className="bg-[#1F1F1F] border-b border-[#2D2D2D] px-4 md:px-6 py-2.5 text-sm"
      data-testid="breadcrumbs"
    >
      {/* Mobile: compact back pill (parent only) */}
      <div className="flex md:hidden items-center">
        {parent ? (
          <Link
            to={parent.href}
            className="inline-flex items-center gap-1.5 text-[#1ABC9C] hover:text-[#1ABC9C]/80 font-medium"
            data-testid="breadcrumb-back-mobile"
          >
            <ArrowLeft className="w-4 h-4" />
            {parent.label}
          </Link>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[#A1A1AA]">
            <Home className="w-3.5 h-3.5" />
            {last.label}
          </span>
        )}
      </div>

      {/* Desktop: full trail */}
      <ol className="hidden md:flex items-center gap-1.5 text-[#A1A1AA]">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={c.href} className="inline-flex items-center gap-1.5">
              {idx === 0 && <Home className="w-3.5 h-3.5 mr-0.5" />}
              {isLast ? (
                <span className="text-white font-medium" data-testid="breadcrumb-current">{c.label}</span>
              ) : (
                <Link
                  to={c.href}
                  className="hover:text-white transition-colors"
                  data-testid={`breadcrumb-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c.label}
                </Link>
              )}
              {!isLast && <ChevronRight className="w-3.5 h-3.5 text-[#52525B]" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
